/**
 * Modal para seleccionar un asesor a suplantar
 * Accessible solo para SYSADMIN cuando no hay impersonación activa
 */

import { Badge, Button, Input, Modal } from '@/components/ui';
import { useImpersonation } from '@/hooks/useImpersonation';
import { useAsesores } from '@/hooks/useSysadmin';
import { AlertCircle, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

interface ImpersonationModalProps {
  open: boolean;
  onClose: () => void;
}

export function ImpersonationModal({ open, onClose }: ImpersonationModalProps) {
  const { impersonate, canImpersonate } = useImpersonation();
  const { asesores, isLoading: loadingAsesores } = useAsesores();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtrar asesores por búsqueda
  const filtered = useMemo(() => {
    if (!search) return asesores;
    const q = search.toLowerCase();
    return asesores.filter((a) => a.nombre.toLowerCase().includes(q) || a.correo.toLowerCase().includes(q));
  }, [asesores, search]);

  // Si no puede impersonar, no mostrar el modal
  if (!canImpersonate) return null;

  // Solo mostrar asesores activos
  const activos = filtered.filter((a) => a.estado);

  const handleImpersonate = async () => {
    if (!selectedId) return;

    setLoading(true);
    setError(null);

    try {
      await impersonate(selectedId);
      onClose();
      // Redirigir al dashboard del asesor
      window.location.replace('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al iniciar impersonación');
      console.error('Error al iniciar impersonación:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSearch('');
    setSelectedId(null);
    setError(null);
    onClose();
  };

  return (
    <Modal isOpen={open} onClose={handleClose} title="Suplantar Asesor" size="md">
      <div className="space-y-4">
        {/* Description */}
        <p className="text-sm text-gray-600">
          Selecciona un asesor para actuar en su nombre. Podrás ver y modificar todo como si fueras esa persona.
        </p>

        {/* Error message */}
        {error && (
          <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input placeholder="Buscar por nombre o correo..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>

        {/* Asesores list */}
        <div className="max-h-64 overflow-y-auto space-y-2 border border-gray-200 rounded-lg p-2">
          {loadingAsesores ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center text-gray-500">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent mx-auto mb-2"></div>
                <p className="text-sm">Cargando asesores...</p>
              </div>
            </div>
          ) : activos.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500">{search ? 'No se encontraron asesores activos' : 'No hay asesores disponibles'}</p>
            </div>
          ) : (
            activos.map((asesor) => (
              <div
                key={asesor.asesorId}
                onClick={() => setSelectedId(asesor.asesorId)}
                className={`p-3 rounded-lg cursor-pointer border-2 transition-all ${
                  selectedId === asesor.asesorId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{asesor.nombre}</p>
                    <p className="text-sm text-gray-500">{asesor.correo}</p>
                    {asesor.telefono && <p className="text-xs text-gray-400">{asesor.telefono}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-1 ml-2">
                    <Badge variant="green">Activo</Badge>
                    <span className="text-xs text-gray-500">{asesor.cantidadEmpresas} empresas</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 justify-end pt-2 border-t">
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleImpersonate} disabled={!selectedId || loading} className="bg-amber-500 hover:bg-amber-600 text-black">
            {loading ? (
              <>
                <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></span>
                Iniciando...
              </>
            ) : (
              'Acceder como...'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
